import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { X, Plus, Edit, Trash2, Copy, Eye, EyeOff, ArrowRight } from "lucide-react";
import { ProductionBoard, Reports, InventoryTab, ConfigTab, ShipmentsTab } from "@/pages/AdminPhase3";
import AdminBusinessSettings from "@/pages/AdminBusinessSettings";

const STAGES = ["payment_pending","artwork_upload","artwork_review","proof_approval","production_queue","printing","quality_inspection","packaging","shipping","delivered","completed"];

const PARENT_TYPE_OPTIONS = [
  { value: "printing", label: "Printing" },
  { value: "graphic_design", label: "Graphic Design" },
  { value: "website_design", label: "Website Design" },
];
const PRODUCT_STATUS_OPTIONS = [
  { value: "active", label: "Active" }, { value: "hidden", label: "Hidden" },
  { value: "coming_soon", label: "Coming Soon" }, { value: "archived", label: "Archived" },
];
const RULE_TYPE_OPTIONS = [
  { value: "flat_add", label: "Flat Add ($)" }, { value: "percent_add", label: "Percent (%)" },
  { value: "tier_multiplier", label: "Tier Multiplier" }, { value: "option_add", label: "Option Add" },
];
const CARRIER_OPTIONS = [
  { value: "usps", label: "USPS" }, { value: "ups", label: "UPS" }, { value: "fedex", label: "FedEx" },
  { value: "pickup", label: "Studio Pickup" }, { value: "local", label: "Local Delivery" },
];
const PRICING_FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "rule_type", label: "Rule Type", type: "select", options: RULE_TYPE_OPTIONS, required: true },
  { k: "amount", label: "Amount", type: "number", step: "0.01", required: true },
  { k: "parent_type", label: "Applies To Type", type: "select", options: PARENT_TYPE_OPTIONS },
  { k: "product_id", label: "Product ID (optional)" },
  { k: "option_name", label: "Option Name (optional)" },
  { k: "option_value", label: "Option Value (optional)" },
  { k: "quantity_min", label: "Min Qty", type: "number" },
  { k: "quantity_max", label: "Max Qty", type: "number" },
  { k: "priority", label: "Priority", type: "number", default: 0 },
];
const MATERIAL_FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "description", label: "Description" },
  { k: "sku", label: "SKU" },
  { k: "price_delta", label: "Price Delta ($)", type: "number", step: "0.01", default: 0 },
];
const FINISH_FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "description", label: "Description" },
  { k: "price_delta", label: "Price Delta ($)", type: "number", step: "0.01", default: 0 },
];
const SIZE_FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "dimensions", label: "Dimensions", required: true },
  { k: "price_delta", label: "Price Delta ($)", type: "number", step: "0.01", default: 0 },
];
const TURNAROUND_FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "days", label: "Days", type: "number", required: true, default: 5 },
  { k: "price_multiplier", label: "Price Multiplier", type: "number", step: "0.01", default: 1 },
];
const SHIPPING_FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "carrier", label: "Carrier", type: "select", options: CARRIER_OPTIONS, required: true },
  { k: "service", label: "Service" },
  { k: "base_price", label: "Base Price ($)", type: "number", step: "0.01", default: 0 },
  { k: "per_item_price", label: "Per Item ($)", type: "number", step: "0.01", default: 0 },
  { k: "transit_days", label: "Transit Days", type: "number", default: 3 },
];


function Input({ label, ...rest }) {
  return (
    <div>
      <label className="text-[10px] font-mono tracking-[0.3em] uppercase">{label}</label>
      <input {...rest} className="mt-1 w-full border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]" />
    </div>
  );
}
function Textarea({ label, ...rest }) {
  return (
    <div>
      <label className="text-[10px] font-mono tracking-[0.3em] uppercase">{label}</label>
      <textarea {...rest} rows={4} className="mt-1 w-full border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059] resize-none" />
    </div>
  );
}
function Select({ label, options, ...rest }) {
  return (
    <div>
      <label className="text-[10px] font-mono tracking-[0.3em] uppercase">{label}</label>
      <select {...rest} className="mt-1 w-full border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [pages, setPages] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [blog, setBlog] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [dialog, setDialog] = useState(null); // { type, data }
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiReply, setAiReply] = useState("");

  const refresh = useCallback(() => {
    api.get("/analytics/summary").then(r => setStats(r.data));
    api.get("/orders").then(r => setOrders(r.data));
    api.get("/quotes").then(r => setQuotes(r.data));
    api.get("/products?include_hidden=true").then(r => setProducts(r.data));
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/printers").then(r => setPrinters(r.data));
    api.get("/cms/pages").then(r => setPages(r.data));
    api.get("/portfolio").then(r => setPortfolio(r.data));
    api.get("/blog?published_only=false").then(r => setBlog(r.data));
    api.get("/testimonials/all").then(r => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then(r => setFaqs(r.data));
    api.get("/industries").then(r => setIndustries(r.data));
  }, []);

  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (!user) return;
    if (!["admin","staff","super_admin"].includes(user.role)) { nav("/dashboard"); return; }
    refresh();
  }, [user, nav, refresh]);

  // Actions
  const priceQuote = async (q) => {
    const price = prompt(`Set price for "${q.subject}"`, q.price || "");
    if (!price) return;
    const notes = prompt("Admin notes (optional)", q.admin_notes || "") || "";
    await api.patch(`/quotes/${q.id}/price`, { price: Number(price), admin_notes: notes });
    toast.success("Quote priced"); refresh();
  };
  const convertQuote = async (id) => {
    try { await api.post(`/quotes/${id}/convert-to-order`); toast.success("Quote converted to order"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
  };
  const updateOrderStage = async (id, status) => { await api.patch(`/orders/${id}/status`, { status }); toast.success("Stage updated"); refresh(); };
  const uploadProof = async (order_id, e) => {
    const file = e.target.files[0]; if (!file) return;
    const notes = prompt("Proof notes (optional)") || "";
    const fd = new FormData(); fd.append("file", file); fd.append("notes", notes);
    try { await api.post(`/orders/${order_id}/upload-proof`, fd); toast.success("Proof uploaded"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };
  const askAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiReply("Thinking...");
    try {
      const { data } = await api.post("/ai/chat", { session_id: "admin-" + user.id, message: aiPrompt, context: "admin" });
      setAiReply(data.reply);
    } catch { setAiReply("AI unavailable"); }
  };

  // Delete helpers per resource
  const del = async (endpoint, id, label) => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    try { await api.delete(`/${endpoint}/${id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const duplicateProduct = async (id) => { await api.post(`/products/${id}/duplicate`); toast.success("Duplicated"); refresh(); };
  const toggleProductStatus = async (p) => {
    const next = p.status === "active" ? "hidden" : "active";
    await api.patch(`/products/${p.id}/status`, { status: next });
    toast.success(`Product ${next}`); refresh();
  };
  const changeRole = async (uid, currentRole) => {
    const role = prompt("New role: customer | staff | admin | super_admin", currentRole || "customer");
    if (!role) return;
    try { await api.patch(`/customers/${uid}/role`, { role }); toast.success("Role updated"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Only super admin can change roles"); }
  };

  if (!user || !["admin","staff","super_admin"].includes(user.role)) return <div className="p-20 text-center font-mono text-xs">LOADING...</div>;

  const tabs = ["overview","settings","production","orders","shipments","quotes","products","pricing","materials","finishes","sizes","turnaround","shipping-options","inventory","categories","printers","customers","cms","portfolio","blog","testimonials","faqs","industries","reports","ai"];

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Admin — {user.role}</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Control Center</h1>
        </div>
        <Link to="/" className="text-xs font-mono tracking-[0.25em] uppercase text-[#555]" data-testid="admin-view-site">View Site →</Link>
      </div>

      <div className="mt-10 border-b border-[#E5E5E5] flex flex-wrap gap-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`pb-3 text-xs font-mono tracking-[0.25em] uppercase whitespace-nowrap ${tab === t ? "border-b-2 border-[#C5A059] text-[#C5A059]" : "text-[#555] hover:text-[#0a0a0a]"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Total Orders", stats.total_orders], ["Paid Orders", stats.paid_orders], ["Customers", stats.total_customers], ["Revenue", `$${(stats.revenue||0).toFixed(2)}`], ["Pending Quotes", stats.pending_quotes]].map(([k,v]) => (
              <div key={k} className="border border-[#E5E5E5] p-6" data-testid={`stat-${k.toLowerCase().replace(/\s+/g,'-')}`}>
                <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]">{k}</p>
                <p className="font-serif text-4xl mt-3">{v ?? 0}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && <AdminBusinessSettings />}

        {tab === "orders" && (
          <div className="space-y-3">
            {orders.map(o => (
              <div key={o.id} className="border border-[#E5E5E5] p-5" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <p className="font-mono text-xs uppercase text-[#C5A059]">#{o.id.slice(0,8)} · {o.workflow_stage}</p>
                    <p className="font-serif text-lg">{o.customer_email || "guest"} · ${o.amount.toFixed(2)}</p>
                    <p className="text-xs text-[#888]">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select onChange={(e) => updateOrderStage(o.id, e.target.value)} defaultValue={o.workflow_stage} className="border border-[#E5E5E5] px-3 py-2 text-sm bg-white" data-testid={`admin-order-stage-${o.id}`}>
                      {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                    </select>
                    <label className="text-xs font-mono tracking-[0.2em] uppercase border border-[#0a0a0a] px-3 py-2 cursor-pointer hover:bg-[#FAFAFA]" data-testid={`admin-upload-proof-${o.id}`}>
                      Upload Proof
                      <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => uploadProof(o.id, e)} />
                    </label>
                  </div>
                </div>
                {o.approval_history && o.approval_history.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs font-mono uppercase tracking-widest text-[#555] cursor-pointer">History ({o.approval_history.length})</summary>
                    <div className="mt-2 space-y-1 text-xs text-[#555] font-mono">
                      {o.approval_history.map((h) => <p key={`${h.at}-${h.event}`}>· {new Date(h.at).toLocaleString()} — {h.event.replace(/_/g," ")} by {h.by}{h.notes ? ` — "${h.notes}"` : ""}</p>)}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="space-y-3">
            {quotes.map(q => (
              <div key={q.id} className="border border-[#E5E5E5] p-5" data-testid={`admin-quote-${q.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <p className="font-mono text-xs uppercase text-[#C5A059]">{q.status} · {q.product_type}</p>
                    <p className="font-serif text-xl">{q.subject}</p>
                    <p className="text-xs text-[#555]">{q.contact_name} · {q.contact_email}{q.contact_phone ? ` · ${q.contact_phone}` : ""}</p>
                    <p className="text-sm text-[#555] mt-2">{q.details}</p>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs font-mono text-[#888]">
                      {q.quantity && <span>Qty {q.quantity}</span>}
                      {q.dimensions && <span>{q.dimensions}</span>}
                      {q.material && <span>{q.material}</span>}
                      {q.finishing && <span>{q.finishing}</span>}
                      {q.deadline && <span>Deadline {q.deadline}</span>}
                      {q.delivery_method && <span>{q.delivery_method}</span>}
                      {q.budget && <span>Budget ${q.budget}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 min-w-[160px]">
                    {q.price ? <p className="font-mono text-2xl text-[#C5A059] text-right">${q.price.toFixed(2)}</p> : null}
                    <button onClick={() => priceQuote(q)} data-testid={`admin-quote-price-${q.id}`} className="text-xs font-mono tracking-[0.2em] uppercase bg-[#0a0a0a] text-white px-4 py-2">{q.price ? "Revise Price" : "Set Price"}</button>
                    {q.price && q.status !== "converted" && (
                      <button onClick={() => convertQuote(q.id)} data-testid={`admin-quote-convert-${q.id}`} className="text-xs font-mono tracking-[0.2em] uppercase border border-[#0a0a0a] px-4 py-2 inline-flex items-center gap-1 justify-center">Convert to Order <ArrowRight size={12} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div>
            <button onClick={() => setDialog({ type: "product", data: null })} data-testid="admin-add-product" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#C5A059] hover:text-[#0a0a0a]"><Plus size={14} /> New Product</button>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {products.map(p => (
                <div key={p.id} className="border border-[#E5E5E5] flex flex-col" data-testid={`admin-product-${p.slug}`}>
                  <img src={p.image_url} className="aspect-video object-cover w-full" alt={p.name} />
                  <div className="p-4 flex-1 flex flex-col">
                    <p className="font-serif text-lg">{p.name}</p>
                    <p className="font-mono text-xs text-[#888]">{p.parent_type} · ${p.base_price.toFixed(2)} · {p.status}</p>
                    <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-[#F5F5F5]">
                      <button onClick={() => setDialog({ type: "product", data: p })} data-testid={`admin-edit-product-${p.slug}`} className="text-xs px-2 py-1 border border-[#E5E5E5] inline-flex items-center gap-1 hover:border-[#0a0a0a]"><Edit size={12} /> Edit</button>
                      <button onClick={() => duplicateProduct(p.id)} data-testid={`admin-dup-product-${p.slug}`} className="text-xs px-2 py-1 border border-[#E5E5E5] inline-flex items-center gap-1 hover:border-[#0a0a0a]"><Copy size={12} /> Copy</button>
                      <button onClick={() => toggleProductStatus(p)} data-testid={`admin-toggle-product-${p.slug}`} className="text-xs px-2 py-1 border border-[#E5E5E5] inline-flex items-center gap-1 hover:border-[#0a0a0a]">{p.status === "active" ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Publish</>}</button>
                      <button onClick={() => del("products", p.id, "product")} data-testid={`admin-del-product-${p.slug}`} className="text-xs px-2 py-1 border border-[#9A1F1F] text-[#9A1F1F] inline-flex items-center gap-1"><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "categories" && (
          <div>
            <button onClick={() => setDialog({ type: "category", data: null })} data-testid="admin-add-category" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Category</button>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {categories.map(c => (
                <div key={c.id} className="border border-[#E5E5E5] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#C5A059]">{c.parent_type}</p>
                  <p className="font-serif text-lg mt-1">{c.name}</p>
                  <p className="text-xs text-[#888] font-mono">/{c.slug}</p>
                  <button onClick={() => del("categories", c.id, "category")} className="mt-2 text-xs text-[#9A1F1F]">Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "printers" && (
          <div>
            <button onClick={() => setDialog({ type: "printer", data: null })} data-testid="admin-add-printer" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Printer</button>
            <div className="space-y-3">
              {printers.map(pr => (
                <div key={pr.id} className="border border-[#E5E5E5] p-4 flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <p className="font-serif text-lg">{pr.name} — {pr.model}</p>
                    <p className="text-xs text-[#555]">Max: {pr.max_paper_size} · Capabilities: {pr.capabilities.join(", ")}</p>
                    <p className="text-[10px] font-mono uppercase mt-1 text-[#C5A059]">{pr.active ? "active" : "inactive"}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setDialog({ type: "printer", data: pr })} className="text-xs px-3 py-2 border border-[#E5E5E5]"><Edit size={12} /></button>
                    <button onClick={() => del("printers", pr.id, "printer")} className="text-xs px-3 py-2 border border-[#9A1F1F] text-[#9A1F1F]"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "customers" && (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E5E5E5] text-left text-xs font-mono uppercase tracking-widest text-[#555]"><th className="py-3">Name</th><th>Email</th><th>Role</th><th>Joined</th><th></th></tr></thead>
            <tbody>{customers.map(c => (
              <tr key={c.id} className="border-b border-[#F5F5F5]"><td className="py-3">{c.name}</td><td>{c.email}</td><td className="font-mono text-xs uppercase text-[#C5A059]">{c.role}</td><td className="font-mono text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                <td>{user.role === "super_admin" && <button onClick={() => changeRole(c.id, c.role)} className="text-xs underline">Change role</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === "cms" && (
          <div>
            <button onClick={() => setDialog({ type: "cms", data: null })} data-testid="admin-add-cms" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Page</button>
            <div className="space-y-3">
              {pages.map(pg => (
                <div key={pg.id} className="border border-[#E5E5E5] p-4 flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <p className="font-serif text-lg">{pg.title}</p>
                    <p className="text-xs text-[#888] font-mono">/{pg.slug}</p>
                    <p className="text-sm text-[#555] mt-2 line-clamp-2">{pg.content}</p>
                  </div>
                  <button onClick={() => setDialog({ type: "cms", data: pg })} className="text-xs px-3 py-2 border border-[#E5E5E5]"><Edit size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "portfolio" && (
          <div>
            <button onClick={() => setDialog({ type: "portfolio", data: null })} data-testid="admin-add-portfolio" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Project</button>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {portfolio.map(p => (
                <div key={p.id} className="border border-[#E5E5E5]">
                  <img src={p.image_url} className="aspect-video object-cover w-full" alt={p.title} />
                  <div className="p-3">
                    <p className="font-serif text-lg">{p.title}</p>
                    <p className="text-xs text-[#888]">{p.category}</p>
                    <button onClick={() => del("portfolio", p.id, "project")} className="mt-2 text-xs text-[#9A1F1F]">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "blog" && (
          <div>
            <button onClick={() => setDialog({ type: "blog", data: null })} data-testid="admin-add-blog" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Post</button>
            <div className="space-y-3">
              {blog.map(b => (
                <div key={b.id} className="border border-[#E5E5E5] p-4 flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <p className="font-serif text-lg">{b.title}</p>
                    <p className="text-xs text-[#888] font-mono">/{b.slug} · {b.published ? "published" : "draft"}</p>
                    <p className="text-sm text-[#555] mt-1">{b.excerpt}</p>
                  </div>
                  <button onClick={() => del("blog", b.id, "post")} className="text-xs px-3 py-2 border border-[#9A1F1F] text-[#9A1F1F]"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "testimonials" && (
          <div>
            <button onClick={() => setDialog({ type: "testimonial", data: null })} data-testid="admin-add-testimonial" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Testimonial</button>
            <div className="grid md:grid-cols-2 gap-4">
              {testimonials.map(t => (
                <div key={t.id} className="border border-[#E5E5E5] p-5">
                  <p className="font-serif italic text-lg">"{t.quote}"</p>
                  <div className="mt-4 flex justify-between items-center">
                    <p className="text-sm">{t.author} · <span className="text-[#888]">{t.company}</span></p>
                    <button onClick={() => del("testimonials", t.id, "testimonial")} className="text-xs text-[#9A1F1F]">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "faqs" && (
          <div>
            <button onClick={() => setDialog({ type: "faq", data: null })} data-testid="admin-add-faq" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New FAQ</button>
            <div className="space-y-3">
              {faqs.map(f => (
                <div key={f.id} className="border border-[#E5E5E5] p-4">
                  <p className="font-serif text-lg">{f.question}</p>
                  <p className="text-sm text-[#555] mt-1">{f.answer}</p>
                  <p className="text-xs text-[#888] font-mono mt-1">{f.category}</p>
                  <button onClick={() => del("faqs", f.id, "FAQ")} className="mt-2 text-xs text-[#9A1F1F]">Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "industries" && (
          <div>
            <button onClick={() => setDialog({ type: "industry", data: null })} data-testid="admin-add-industry" className="mb-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold"><Plus size={14} /> New Industry</button>
            <div className="grid md:grid-cols-3 gap-4">
              {industries.map(ind => (
                <div key={ind.id} className="border border-[#E5E5E5]">
                  <img src={ind.image_url} className="aspect-video object-cover w-full" alt={ind.name} />
                  <div className="p-3">
                    <p className="font-serif text-lg">{ind.name}</p>
                    <p className="text-xs text-[#888]">{ind.description}</p>
                    <button onClick={() => del("industries", ind.id, "industry")} className="mt-2 text-xs text-[#9A1F1F]">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "ai" && (
          <div className="max-w-[820px]">
            <p className="text-sm text-[#555] mb-4">Content-assist powered by Claude Sonnet 4.5. Ask for product copy, blog outlines, or marketing angles.</p>
            <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} rows={4} placeholder="e.g. Write 3 tagline options for a premium letterpress business card…" className="w-full border border-[#E5E5E5] bg-[#FAFAFA] p-4 focus:border-[#C5A059] outline-none" data-testid="admin-ai-prompt" />
            <button onClick={askAI} data-testid="admin-ai-submit" className="mt-3 bg-[#0a0a0a] text-white px-8 py-3 text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#C5A059] hover:text-[#0a0a0a]">Generate</button>
            {aiReply && <div className="mt-6 border border-[#E5E5E5] p-6 whitespace-pre-wrap text-[#333] leading-relaxed" data-testid="admin-ai-reply">{aiReply}</div>}
          </div>
        )}

        {tab === "production" && <ProductionBoard />}
        {tab === "reports" && <Reports />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "shipments" && <ShipmentsTab />}
        {tab === "pricing" && <ConfigTab endpoint="pricing-rules" label="Pricing Rule" testid="pr" fields={PRICING_FIELDS} />}
        {tab === "materials" && <ConfigTab endpoint="materials" label="Material" testid="mat" fields={MATERIAL_FIELDS} />}
        {tab === "finishes" && <ConfigTab endpoint="finishes" label="Finish" testid="fin" fields={FINISH_FIELDS} />}
        {tab === "sizes" && <ConfigTab endpoint="sizes" label="Size" testid="siz" fields={SIZE_FIELDS} />}
        {tab === "turnaround" && <ConfigTab endpoint="turnaround-options" label="Turnaround" testid="tur" fields={TURNAROUND_FIELDS} />}
        {tab === "shipping-options" && <ConfigTab endpoint="shipping-options" label="Shipping Option" testid="so" fields={SHIPPING_FIELDS} />}
      </div>

      {dialog && <ResourceDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refresh(); }} categories={categories} />}
    </section>
  );
}

// -------------------- Resource dialog --------------------
function ResourceDialog({ dialog, onClose, onSaved, categories }) {
  const { type, data } = dialog;
  const isEdit = !!data;
  const [form, setForm] = useState(() => defaultForm(type, data));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const cfg = endpointConfig(type);
      const payload = cfg.prepare ? cfg.prepare(form) : form;
      if (isEdit) {
        if (cfg.updatePath) await api.patch(cfg.updatePath(data), payload);
        else await api.post(cfg.create, payload); // upsert (cms)
      } else {
        await api.post(cfg.create, payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const title = { product: "Product", category: "Category", printer: "Printer", cms: "Page",
                  portfolio: "Portfolio Project", blog: "Blog Post", testimonial: "Testimonial",
                  faq: "FAQ", industry: "Industry" }[type];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[720px] max-h-[90vh] overflow-y-auto bg-white rounded-none border border-[#E5E5E5]">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">{isEdit ? "Edit" : "New"} {title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {type === "product" && (
            <>
              <Input label="Name" required value={form.name} onChange={e => set("name", e.target.value)} data-testid="dialog-product-name" />
              <Input label="Slug" required value={form.slug} onChange={e => set("slug", e.target.value)} data-testid="dialog-product-slug" />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Type" value={form.parent_type} onChange={e => set("parent_type", e.target.value)} options={PARENT_TYPE_OPTIONS} />
                <Select label="Status" value={form.status} onChange={e => set("status", e.target.value)} options={PRODUCT_STATUS_OPTIONS} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input label="Base Price ($)" type="number" step="0.01" value={form.base_price} onChange={e => set("base_price", e.target.value)} data-testid="dialog-product-price" />
                <Input label="Min Qty" type="number" value={form.min_quantity} onChange={e => set("min_quantity", e.target.value)} />
                <Input label="Lead Days" type="number" value={form.lead_time_days} onChange={e => set("lead_time_days", e.target.value)} />
              </div>
              <Input label="Image URL" value={form.image_url} onChange={e => set("image_url", e.target.value)} />
              <Textarea label="Description" value={form.description} onChange={e => set("description", e.target.value)} />
              <Input label="Required Printer Capabilities (comma-separated)" value={form.required_printer_capabilities_text} onChange={e => set("required_printer_capabilities_text", e.target.value)} placeholder="a4_color, cardstock" />
              <Textarea label='Options JSON (array of {name, values})' value={form.options_text} onChange={e => set("options_text", e.target.value)} placeholder='[{"name":"Finish","values":["Matte","Glossy"]}]' />
            </>
          )}
          {type === "category" && (
            <>
              <Input label="Name" required value={form.name} onChange={e => set("name", e.target.value)} />
              <Input label="Slug" required value={form.slug} onChange={e => set("slug", e.target.value)} />
              <Select label="Type" value={form.parent_type} onChange={e => set("parent_type", e.target.value)} options={PARENT_TYPE_OPTIONS} />
              <Textarea label="Description" value={form.description} onChange={e => set("description", e.target.value)} />
              <Input label="Image URL" value={form.image_url} onChange={e => set("image_url", e.target.value)} />
            </>
          )}
          {type === "printer" && (
            <>
              <Input label="Name" required value={form.name} onChange={e => set("name", e.target.value)} />
              <Input label="Model" required value={form.model} onChange={e => set("model", e.target.value)} />
              <Input label="Max Paper Size" value={form.max_paper_size} onChange={e => set("max_paper_size", e.target.value)} />
              <Input label="Capabilities (comma-separated)" value={form.capabilities_text} onChange={e => set("capabilities_text", e.target.value)} placeholder="a4_color, cardstock, borderless_print" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} /> Active</label>
            </>
          )}
          {type === "cms" && (
            <>
              <Input label="Slug" required value={form.slug} onChange={e => set("slug", e.target.value)} />
              <Input label="Title" required value={form.title} onChange={e => set("title", e.target.value)} />
              <Textarea label="Content" value={form.content} onChange={e => set("content", e.target.value)} />
              <Input label="Meta Description" value={form.meta_description} onChange={e => set("meta_description", e.target.value)} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} /> Published</label>
            </>
          )}
          {type === "portfolio" && (
            <>
              <Input label="Title" required value={form.title} onChange={e => set("title", e.target.value)} />
              <Select label="Category" value={form.category} onChange={e => set("category", e.target.value)} options={PARENT_TYPE_OPTIONS} />
              <Textarea label="Description" value={form.description} onChange={e => set("description", e.target.value)} />
              <Input label="Image URL" required value={form.image_url} onChange={e => set("image_url", e.target.value)} />
              <Input label="Tags (comma-separated)" value={form.tags_text} onChange={e => set("tags_text", e.target.value)} />
            </>
          )}
          {type === "blog" && (
            <>
              <Input label="Slug" required value={form.slug} onChange={e => set("slug", e.target.value)} />
              <Input label="Title" required value={form.title} onChange={e => set("title", e.target.value)} />
              <Textarea label="Excerpt" value={form.excerpt} onChange={e => set("excerpt", e.target.value)} />
              <Textarea label="Content" value={form.content} onChange={e => set("content", e.target.value)} />
              <Input label="Cover Image URL" value={form.cover_image} onChange={e => set("cover_image", e.target.value)} />
              <Input label="Tags (comma-separated)" value={form.tags_text} onChange={e => set("tags_text", e.target.value)} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} /> Published</label>
            </>
          )}
          {type === "testimonial" && (
            <>
              <Input label="Author" required value={form.author} onChange={e => set("author", e.target.value)} />
              <Input label="Role" value={form.role} onChange={e => set("role", e.target.value)} />
              <Input label="Company" value={form.company} onChange={e => set("company", e.target.value)} />
              <Textarea label="Quote" required value={form.quote} onChange={e => set("quote", e.target.value)} />
              <Input label="Rating (1-5)" type="number" min="1" max="5" value={form.rating} onChange={e => set("rating", e.target.value)} />
              <Input label="Image URL" value={form.image_url} onChange={e => set("image_url", e.target.value)} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} /> Published</label>
            </>
          )}
          {type === "faq" && (
            <>
              <Input label="Question" required value={form.question} onChange={e => set("question", e.target.value)} />
              <Textarea label="Answer" required value={form.answer} onChange={e => set("answer", e.target.value)} />
              <Input label="Category" value={form.category} onChange={e => set("category", e.target.value)} placeholder="e.g. orders, artwork, shipping" />
              <Input label="Order" type="number" value={form.order} onChange={e => set("order", e.target.value)} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} /> Published</label>
            </>
          )}
          {type === "industry" && (
            <>
              <Input label="Name" required value={form.name} onChange={e => set("name", e.target.value)} />
              <Input label="Slug" required value={form.slug} onChange={e => set("slug", e.target.value)} />
              <Textarea label="Description" value={form.description} onChange={e => set("description", e.target.value)} />
              <Input label="Image URL" value={form.image_url} onChange={e => set("image_url", e.target.value)} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} /> Published</label>
            </>
          )}
          <DialogFooter className="pt-4 border-t border-[#E5E5E5] flex gap-3">
            <button type="button" onClick={onClose} className="px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold border border-[#0a0a0a]">Cancel</button>
            <button disabled={saving} data-testid="dialog-save" className="px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a]">{saving ? "Saving..." : "Save"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function defaultForm(type, data) {
  const base = data ? { ...data } : {};
  const list = (arr) => Array.isArray(arr) ? arr.join(", ") : "";
  switch (type) {
    case "product":
      return {
        name: "", slug: "", parent_type: "printing", status: "active", base_price: 0, min_quantity: 1, lead_time_days: 3,
        image_url: "", description: "", category_id: null,
        required_printer_capabilities_text: list(base.required_printer_capabilities),
        options_text: base.options ? JSON.stringify(base.options) : "[]",
        ...base,
      };
    case "category":
      return { name: "", slug: "", parent_type: "printing", description: "", image_url: "", ...base };
    case "printer":
      return { name: "", model: "", max_paper_size: "A4", active: true, capabilities_text: list(base.capabilities), ...base };
    case "cms":
      return { slug: "", title: "", content: "", meta_description: "", published: true, ...base };
    case "portfolio":
      return { title: "", category: "printing", description: "", image_url: "", tags_text: list(base.tags), ...base };
    case "blog":
      return { slug: "", title: "", excerpt: "", content: "", cover_image: "", published: true, tags_text: list(base.tags), ...base };
    case "testimonial":
      return { author: "", role: "", company: "", quote: "", rating: 5, image_url: "", published: true, ...base };
    case "faq":
      return { question: "", answer: "", category: "general", order: 0, published: true, ...base };
    case "industry":
      return { name: "", slug: "", description: "", image_url: "", icon: "", published: true, ...base };
    default: return {};
  }
}

function endpointConfig(type) {
  const csv = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);
  switch (type) {
    case "product":
      return {
        create: "/products",
        updatePath: (d) => `/products/${d.id}`,
        prepare: (f) => {
          let options = [];
          try { options = JSON.parse(f.options_text || "[]"); } catch { options = []; }
          return {
            name: f.name, slug: f.slug, category_id: f.category_id || null, parent_type: f.parent_type,
            description: f.description || "", base_price: Number(f.base_price) || 0,
            image_url: f.image_url || "", status: f.status, options,
            required_printer_capabilities: csv(f.required_printer_capabilities_text),
            lead_time_days: Number(f.lead_time_days) || 3, min_quantity: Number(f.min_quantity) || 1,
          };
        },
      };
    case "category":
      return { create: "/categories", updatePath: null, prepare: (f) => ({ name: f.name, slug: f.slug, description: f.description || "", parent_type: f.parent_type, image_url: f.image_url || "" }) };
    case "printer":
      return { create: "/printers", updatePath: (d) => `/printers/${d.id}`, prepare: (f) => ({ name: f.name, model: f.model, capabilities: csv(f.capabilities_text), max_paper_size: f.max_paper_size || "A4", active: !!f.active }) };
    case "cms":
      return { create: "/cms/pages", updatePath: null, prepare: (f) => ({ slug: f.slug, title: f.title, content: f.content, meta_description: f.meta_description || "", published: !!f.published }) };
    case "portfolio":
      return { create: "/portfolio", updatePath: null, prepare: (f) => ({ title: f.title, category: f.category, description: f.description || "", image_url: f.image_url, tags: csv(f.tags_text) }) };
    case "blog":
      return { create: "/blog", updatePath: null, prepare: (f) => ({ slug: f.slug, title: f.title, excerpt: f.excerpt || "", content: f.content, cover_image: f.cover_image || "", published: !!f.published, tags: csv(f.tags_text) }) };
    case "testimonial":
      return { create: "/testimonials", updatePath: (d) => `/testimonials/${d.id}`, prepare: (f) => ({ author: f.author, role: f.role || "", company: f.company || "", quote: f.quote, rating: Number(f.rating) || 5, image_url: f.image_url || "", published: !!f.published }) };
    case "faq":
      return { create: "/faqs", updatePath: (d) => `/faqs/${d.id}`, prepare: (f) => ({ question: f.question, answer: f.answer, category: f.category || "general", order: Number(f.order) || 0, published: !!f.published }) };
    case "industry":
      return { create: "/industries", updatePath: (d) => `/industries/${d.id}`, prepare: (f) => ({ name: f.name, slug: f.slug, description: f.description || "", image_url: f.image_url || "", icon: f.icon || "", published: !!f.published }) };
    default: return { create: "", updatePath: null };
  }
}
